// React
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// Components
import { GetInTouch, Dialog } from '@mobilitystore/components-interface';

// Actions
import { GOOGLE_RECAPTCHA_SITE_KEY } from '../../../actions/configuration/index';
import { postUserQuery } from '../../../actions/user-service-action';
import { getCountryList } from '../../../actions/CountryAction';

// Data
import { contactValidationList } from '../../user-management/supplier-details/data';

// Assets
import { CDN_LINK } from '../../../assets/img';

// Utils
import {
  handleFormInputChangeUtil,
  handleFormBlurUtil,
  handleGetInTouchSubmitUtil,
  clearFormUtil,
  createCountryOptions,
  updateFormFieldsWithCountryOptions,
  updatePhoneValidation
} from '../utils';

// Data
import { getInTouchDynamicFormFields } from '../data';
import idConstants from '../../../data/idConstants';

const GetInTouchSection = ({ landingContent, contactUsRef }) => {
  const dispatch = useDispatch();
  const { accessToken, countryList } = useSelector((state) => ({
    accessToken: state.loginReducer.accessToken,
    countryList: state.CountryReducer.countryList
  }));

  const recaptchaRef = useRef(null);

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isReCaptchaVerified, setIsReCaptchaVerified] = useState(false);
  const [isDpnAccepted, setIsDpnAccepted] = useState(false);
  const [isGetInTouchSubmitEnabled, setIsGetInTouchSubmitEnabled] = useState(false);
  const [formFields, setFormFields] = useState(getInTouchDynamicFormFields);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTimeout, setDialogTimeout] = useState(null);

  const getInTouchFormValidate = () => {
    const mandatoryFieldsFilled = formFields.every(
      ({ validation, value, error }) =>
        !validation?.rule?.required?.isRequired || (value?.trim() && !error)
    );

    const hasAnyErrors = formFields?.some(
      (field) =>
        field.error ||
        (field.type === 'COMBINE' && field.attributes?.some((attr) => attr.error))
    );

    const isFormValid =
      mandatoryFieldsFilled && !hasAnyErrors && isReCaptchaVerified && isDpnAccepted;
    setIsGetInTouchSubmitEnabled(isFormValid);
  };

  useEffect(() => {
    getInTouchFormValidate();
  }, [formFields, isReCaptchaVerified, isDpnAccepted]);

  useEffect(() => {
    dispatch(getCountryList(idConstants.typeOfUsage.contactNumber));
  }, [dispatch]);

  useEffect(() => {
    if (countryList?.length) {
      const countryOptions = createCountryOptions(countryList);
      setFormFields((prevFields) =>
        updateFormFieldsWithCountryOptions(prevFields, countryOptions, countryList)
      );
    }
  }, [countryList]);

  useEffect(() => {
    return () => {
      if (dialogTimeout) {
        clearTimeout(dialogTimeout);
      }
    };
  }, [dialogTimeout]);

  const handleChange = (token) => {
    if (token) {
      setRecaptchaToken(token);
      setIsReCaptchaVerified(true);
    } else {
      setRecaptchaToken('');
      setIsReCaptchaVerified(false);
    }
  };

  const handleExpire = () => {
    setIsReCaptchaVerified(false);
  };

  const handleRecaptchaError = () => {
    setIsReCaptchaVerified(false);
    setRecaptchaToken('');
  };

  const handleCheckboxChange = (data) => {
    if (data.id === 'get-in-checkbox') {
      setIsDpnAccepted(data.value);
    }
  };

  const handleFormInputChange = (data) => {
    const { name, value } = data;
    name === 'isdCode' &&
      updatePhoneValidation(value?.id, setFormFields, contactValidationList);

    const updatedFields = handleFormInputChangeUtil(data, formFields);
    setFormFields(updatedFields);
  };

  const handleFormBlur = (data) => {
    const updatedFields = handleFormBlurUtil(data, formFields);
    setFormFields(updatedFields);
  };

  const handleGetInTouchSubmit = async () => {
    const response = await handleGetInTouchSubmitUtil({
      formFields,
      recaptchaToken,
      isDpnAccepted,
      accessToken,
      dispatch,
      postUserQuery,
      onSuccess: (currentFields) => clearForm(currentFields)
    });

    if (response?.success) {
      setShowDialog(true);

      const timeoutId = setTimeout(() => {
        setShowDialog(false);
        clearForm(formFields);
      }, 5000);
      setDialogTimeout(timeoutId);
    }
  };

  const handleCloseDialog = () => {
    if (dialogTimeout) {
      clearTimeout(dialogTimeout);
      setDialogTimeout(null);
    }
    setShowDialog(false);
    clearForm(formFields);
  };

  const clearForm = (currentFields = null) => {
    clearFormUtil({
      getInTouchDynamicFormFields,
      setFormFields,
      setRecaptchaToken,
      setIsReCaptchaVerified,
      setIsDpnAccepted: setIsDpnAccepted,
      setIsGetInTouchSubmitEnabled,
      recaptchaRef,
      currentFormFields: currentFields || formFields,
      countryList
    });
  };

  return (
    <>
      <GetInTouch
        className='get-in-touch-section'
        ref={contactUsRef}
        overview={{
          id: '1',
          className: '',
          header: landingContent?.get_in_touch_form?.overview?.title,
          description: landingContent?.get_in_touch_form?.overview?.description,
          media: {
            image: `${CDN_LINK}/seller/${landingContent?.get_in_touch_form?.overview.media?.image}`
          }
        }}
        form={{
          className: 'get-in-touch-form',
          fields: formFields,
          handleInputChange: handleFormInputChange,
          handleBlur: handleFormBlur
        }}
        recaptcha={{
          sitekey: GOOGLE_RECAPTCHA_SITE_KEY,
          className: 'google-recaptcha',
          handleChange: handleChange,
          handleExpire: handleExpire,
          onError: handleRecaptchaError
        }}
        recaptchaRef={recaptchaRef}
        checkbox={{
          id: 'get-in-checkbox',
          checked: isDpnAccepted,
          className: '',
          label: landingContent?.get_in_touch_form?.checkbox?.label,
          linkHref: landingContent?.get_in_touch_form?.checkbox?.href,
          linkText: landingContent?.get_in_touch_form?.checkbox?.text,
          linkTarget: landingContent?.get_in_touch_form?.checkbox?.target,
          linkRel: landingContent?.get_in_touch_form?.checkbox?.rel
        }}
        button={{
          label: landingContent?.get_in_touch_form?.button?.label,
          mode: landingContent?.get_in_touch_form?.button?.mode,
          onClick: handleGetInTouchSubmit
        }}
        handleCheckboxChange={handleCheckboxChange}
        isSubmitEnabled={isGetInTouchSubmitEnabled}
      />

      {showDialog && (
        <div className='thank-you-popup-overlay'>
          <Dialog
            className='thank-you-success-popup'
            show={true}
            status='success'
            title={landingContent?.get_in_touch_form?.thank_you?.title}
            body={landingContent?.get_in_touch_form?.thank_you?.description}
            showCloseIcon={true}
            onClose={handleCloseDialog}
          />
        </div>
      )}
    </>
  );
};

GetInTouchSection.propTypes = {
  landingContent: PropTypes.shape({
    get_in_touch_form: PropTypes.shape({
      overview: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        media: PropTypes.shape({
          image: PropTypes.string
        })
      }),
      checkbox: PropTypes.shape({
        label: PropTypes.string,
        href: PropTypes.string,
        text: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string
      }),
      button: PropTypes.shape({
        label: PropTypes.string,
        mode: PropTypes.string
      }),
      thank_you: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string
      })
    })
  }).isRequired,
  contactUsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]).isRequired
};

export default GetInTouchSection;
